<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="transition duration-200 ease-in"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/95 backdrop-blur-md"
        @click.self="close"
        role="dialog"
        aria-modal="true"
      >
        <!-- Close Button -->
        <button
          @click="close"
          class="absolute top-6 right-6 z-50 inline-flex items-center justify-center size-12 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all duration-300"
          :aria-label="$t('lightbox.close')"
        >
          <Icon name="mdi:close" size="32" />
        </button>

        <!-- Main Content Area -->
        <div class="relative w-full h-full flex flex-col items-center justify-center p-4 md:p-12">
          
          <!-- Image Container with Transition -->
          <div class="relative w-full h-[60dvh] md:h-[80dvh] flex items-center justify-center">
             <Transition
              mode="out-in"
              enter-active-class="transition-all duration-300 ease-out"
              enter-from-class="opacity-0 scale-95 blur-sm"
              enter-to-class="opacity-100 scale-100 blur-0"
              leave-active-class="transition-all duration-200 ease-in"
              leave-from-class="opacity-100 scale-100 blur-0"
              leave-to-class="opacity-0 scale-95 blur-sm"
            >
              <NuxtImg
                :key="currentIndex"
                :src="currentImage.src"
                :alt="currentImage.alt || $t('lightbox.imageFallbackAlt')"
                class="max-w-full max-h-full object-contain shadow-2xl rounded-sm select-none"
                preload
              />
            </Transition>
          </div>

          <!-- Caption -->
          <div class="absolute bottom-8 left-0 right-0 text-center pointer-events-none">
            <Transition
              enter-active-class="transition duration-300 delay-150"
              enter-from-class="opacity-0 translate-y-4"
              enter-to-class="opacity-100 translate-y-0"
            >
               <p v-if="currentImage.alt" :key="currentIndex" class="text-white/90 text-lg font-light tracking-wide drop-shadow-md inline-block bg-black/40 px-4 py-2 rounded-full backdrop-blur-sm">
                {{ currentImage.alt }}
              </p>
            </Transition>
          </div>

          <!-- Navigation Controls -->
          <div class="absolute inset-x-4 top-1/2 -translate-y-1/2 flex justify-between pointer-events-none">
            <button
              v-if="images.length > 1"
              class="pointer-events-auto inline-flex items-center justify-center size-16 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-all duration-300 backdrop-blur-sm -translate-x-2 hover:translate-x-0"
              @click.stop="prev"
              :aria-label="$t('lightbox.prev')"
            >
              <Icon name="mdi:chevron-left" size="48" />
            </button>

            <button
              v-if="images.length > 1"
              class="pointer-events-auto inline-flex items-center justify-center size-16 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-all duration-300 backdrop-blur-sm translate-x-2 hover:translate-x-0"
              @click.stop="next"
              :aria-label="$t('lightbox.next')"
            >
              <Icon name="mdi:chevron-right" size="48" />
            </button>
          </div>
          
           <!-- Image Counter -->
          <div class="absolute top-6 left-6 text-white/50 font-mono text-sm tracking-widest uppercase">
            {{ currentIndex + 1 }} / {{ images.length }}
          </div>

        </div>

        <!-- Thumbnails Strip (Optional, can be toggled or always visible) -->
         <div class="absolute bottom-4 inset-x-0 flex justify-center gap-2 px-4 pointer-events-none opacity-0 hover:opacity-100 transition-opacity duration-300">
             <!-- Simplified dots for cleaner look, or small thumbnails -->
             <div class="flex gap-2 p-2 bg-black/20 backdrop-blur-md rounded-full pointer-events-auto">
                <button
                  v-for="(_, idx) in images"
                  :key="idx"
                  @click="goTo(idx)"
                  class="w-2 h-2 rounded-full transition-all duration-300"
                  :class="idx === currentIndex ? 'bg-white w-6' : 'bg-white/30 hover:bg-white/60'"
                  :aria-label="$t('lightbox.goToImage', { number: idx + 1 })"
                 />
             </div>
         </div>

      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';

interface Image {
  src: string;
  alt?: string;
  [key: string]: unknown;
}

const props = defineProps<{
  images: Image[];
  modelValue: boolean;
  startIndex?: number;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const _currentIndex = ref(props.startIndex || 0);

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
});

const currentIndex = computed({
  get: () => _currentIndex.value,
  set: (val) => _currentIndex.value = val
});

const currentImage = computed(() => props.images[currentIndex.value] || { src: '', alt: '' });

// Sync local index with prop changes if needed, though usually the parent just opens it
watch(() => props.startIndex, (val) => {
  if (val !== undefined) _currentIndex.value = val;
});

watch(isOpen, (val) => {
  if (val) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
});

const next = () => {
    _currentIndex.value = (_currentIndex.value + 1) % props.images.length;
};

const prev = () => {
    _currentIndex.value = (_currentIndex.value - 1 + props.images.length) % props.images.length;
};

const goTo = (index: number) => {
  _currentIndex.value = index;
};

const close = () => {
  isOpen.value = false;
};

const handleKeydown = (e: KeyboardEvent) => {
  if (!isOpen.value) return;

  switch (e.key) {
    case 'ArrowRight':
      next();
      break;
    case 'ArrowLeft':
      prev();
      break;
    case 'Escape':
      close();
      break;
  }
};

onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
  document.body.style.overflow = '';
});
</script>
